import { routeState } from './route'
import { activeStages } from './trip'
import type { DiaryEntry, DiaryTrack, Stage, Trip } from './types'
import { fahrzeug } from './vehicle'

/**
 * Die zweite Hälfte dessen, was in einer Aufzeichnung steht: die Uhr.
 *
 * Aus einer GPX-Datei sind die Kilometer längst herausgeholt (lib/gpx.ts) und
 * werden seit features/map/etappenVergleich.ts der geplanten Strecke
 * gegenübergestellt. Die Zeitstempel liegen daneben und wurden bisher nur
 * angezeigt: fünfhundert Punkte über sechsundsiebzig Minuten, und niemand hat
 * gefragt, ob der Tag so lang war, wie er geplant war.
 *
 * Genau das ist aber die Frage einer Reise über sechzehn Etappen. Die Planung
 * schätzt die Fahrzeit: der Routendienst rechnet sie für einen Personenwagen,
 * und darauf kommt der Zuschlag des Fahrzeugs (vehicleHours in lib/route.ts,
 * surcharge in lib/vehicle.ts) - ein Viertel für den beladenen T3, einmal
 * eingetippt und seither nie überprüft. Die Aufzeichnung weiss es genau.
 *
 * ## Was hier gerechnet wird und was ausdrücklich nicht
 *
 * Gerechnet wird nichts neu. Fahrzeit, Standzeit und Halte stehen fertig im
 * Track (siehe deriveTrack), gerechnet wurde einmal beim Import über alle
 * Rohpunkte. Diese Datei stellt sie der Planung gegenüber und rechnet aus dem
 * Verhältnis hoch, was noch bevorsteht.
 *
 * ## Die dritte Zeit
 *
 * Ein Tag aus mehreren Dateien hat Lücken, und eine Lücke, in der das Fahrzeug
 * den Ort gewechselt hat, ist weder Fahrt noch Halt: gemessen wurde in ihr gar
 * nichts (unrecordedSeconds in types.ts, gerechnet in mergeTrackParts). Sie
 * steht hier als eigene Zahl und nicht in der Fahrzeit - vier Stunden am Steuer
 * auszuweisen, wo zweieinhalb gefahren wurden, wäre die Behauptung einer
 * Messung, die es nicht gibt, und sie ginge über den Faktor in jede
 * Hochrechnung der Reise ein.
 *
 * Die Tagesdauer ändert sie nicht: der Tag war so lang, wie er war (tagH), nur
 * zerfällt er jetzt in drei Teile statt in zwei.
 *
 * Nicht angefasst werden die Kilometer. Die gefahrenen Kilometer einer Etappe
 * kommen aus dem Handwert realKm im Tagebuch und sonst nirgendwoher, die
 * geplanten aus dem Handwert der Etappe - track.distanceKm geht in keine Summe
 * ein und darf es nicht (siehe tripTotals in lib/trip.ts, dessen Test genau das
 * absichert). Die eine Stelle, an der hier eine Streckenzahl der Aufzeichnung
 * vorkommt, ist die Abdeckung: ein Verhältnis, das entscheidet, ob eine
 * Aufzeichnung überhaupt für eine Zeitaussage taugt. Es steht neben den Summen
 * und geht in keine hinein.
 *
 * ## Warum der Zuschlag nicht von selbst nachgezogen wird
 *
 * Weil er eingetippt ist. Nach ein paar Tagen weiss diese Datei besser, wie das
 * Fahrzeug wirklich fährt, als die Zahl im Formular - und trotzdem schreibt
 * hier nichts zurück. Drei Gründe, in dieser Reihenfolge:
 *
 * Erstens würde es nichts reparieren. Die Stunden einer Etappe sind Handwerte
 * (Stage.h); sie sind beim Berechnen der Strecke einmal vorgeschlagen und
 * übernommen worden und stehen seither fest. Ein nachgezogener Zuschlag ändert
 * sie nicht - er ändert nur, was künftigen Strecken vorgeschlagen wird. Eine
 * stille Korrektur sähe deshalb aus wie eine Reparatur des Plans und wäre
 * keine.
 *
 * Zweitens ist die Grundlage schmal und bleibt es. Eine Reise hat sechzehn
 * Etappen, nicht sechzehnhundert; eine einzige Etappe mit Stau oder eine
 * einzige mit freier Autobahn verschiebt den Faktor spürbar. Was aus so wenigen
 * Messungen folgt, ist eine Auskunft und keine Tatsache - und Auskünfte gehören
 * auf den Bildschirm, nicht ins Dokument.
 *
 * Drittens ist das Dokument geteilt. Die Reise geht durch den Abgleich über
 * mehrere Geräte; ein Wert, der sich auf einem Gerät von selbst ändert, kommt
 * auf dem anderen an, ohne dass dort jemand etwas getan hat. Genau davor warnt
 * schon lib/vehicle.ts beim Verbrauch: dort stand einmal eine Empfehlung mit
 * einem Knopf, der die Kraftstoffschätzung verdreifachte.
 *
 * Es gibt deshalb auch keinen Knopf, der den gemessenen Zuschlag übernimmt. Die
 * Zahl steht da, daneben steht die eingetippte, und daneben steht, wo sie zu
 * ändern wäre. Wer sie ändert, tut es im Formular des Fahrzeugs und sieht dabei
 * alles andere, was daran hängt.
 *
 * Was stattdessen hilft, ist die Hochrechnung: nicht "dein Zuschlag ist falsch",
 * sondern "die elf Etappen, die noch kommen, dauern nach dem bisher Gefahrenen
 * so lange". Das ist die Auskunft, die jemand am Morgen braucht, und sie ändert
 * kein einziges gespeichertes Feld.
 */

/** Sekunden je Stunde. Steht einmal hier statt fünfmal in den Rechnungen. */
const STUNDE = 3600

/**
 * Wie viel von einer Etappe eine Aufzeichnung enthalten muss, damit ihre
 * Uhrzeiten etwas über sie sagen.
 *
 * Fünfundachtzig Prozent. Das Gerät wird zu spät eingeschaltet, fällt
 * unterwegs aus oder wird an der Grenze vergessen - dann fehlen Kilometer und
 * mit ihnen die Minuten, in denen sie gefahren wurden. Eine solche Datei ergibt
 * eine Fahrzeit, die kürzer ist als die Fahrt, und daraus abgeleitet ein
 * Fahrzeug, das schneller wäre, als es ist. Genau in die falsche Richtung: die
 * Planung sähe besser aus, als sie ist, und das ist der eine Fehler, den man
 * unterwegs nicht machen darf.
 *
 * Nach oben gibt es keine Schranke, und das ist Absicht. Wer sich verfährt oder
 * einen Umweg nimmt, fährt mehr als geplant - das ist keine kaputte
 * Aufzeichnung, sondern die Reise. Nur das Fehlende ist ein Grund zu schweigen.
 *
 * Warum nicht strenger: fünfzehn Prozent sind auf einer Etappe von 500 km
 * fünfundsiebzig Kilometer, und so viel verpasst kein Gerät unbemerkt. Warum
 * nicht lockerer: eine halbe Stunde Stadtverkehr am Anfang einer Etappe ist
 * schnell ein Zehntel der Strecke und ein Fünftel der Zeit.
 *
 * ## Dieselbe Schranke, zweimal gelesen
 *
 * Gemessen wird die Abdeckung in Kilometern und in Minuten, und beide müssen
 * sie erreichen. Die Kilometer beantworten „steht die Strecke in der Datei“,
 * die Minuten „steht der Tag darin“ - und das kann auseinanderfallen: der
 * 4. August ist an Kilometern vollständig und hat trotzdem 110 Minuten, in
 * denen nichts aufgezeichnet wurde (unrecordedSeconds). Über den Tag sagt seine
 * Fahrzeit deshalb weniger, als sie aussieht: was in der Lücke gefahren wurde,
 * fehlt in ihr, und der Faktor gegen den Routendienst käme zu klein heraus -
 * die Planung sähe wieder besser aus, als sie ist.
 *
 * Zwei Zahlen für dieselbe Frage wären zwei Zahlen zu viel. Es ist dieselbe:
 * wie viel von diesem Tag wirklich in der Datei steht.
 */
export const MIN_ABDECKUNG = 0.85

/**
 * Wie viele Etappen die Eichung mindestens braucht.
 *
 * Drei. Aus einer einzelnen Fahrt folgt nichts: der Heimweg an einem
 * Sonntagabend über die Autobahn und die Fahrt über den Vršič sind dasselbe
 * Fahrzeug und nicht dasselbe Tempo. Erst wenn mehrere Tage in dieselbe
 * Richtung zeigen, ist der Faktor eine Auskunft über das Fahrzeug und nicht
 * über einen Tag.
 */
export const EICHUNG_MIN_ETAPPEN = 3

/**
 * Und wie viele gemessene Fahrstunden.
 *
 * Drei Etappen von je zwanzig Minuten sind drei Etappen und trotzdem keine
 * Grundlage: die Anfahrt zum Stellplatz am Abend ist keine Tagesfahrt. Beide
 * Schranken gelten zusammen.
 */
export const EICHUNG_MIN_STUNDEN = 3

function endlich(v: number | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function runde2(v: number): number {
  return Math.round(v * 100) / 100
}

/**
 * Die Standzeit ohne den Halt am Anfang und am Ende.
 *
 * Eine Pause ist, was die Fahrt unterbricht. Was davor und danach steht, ist
 * keine: das Gerät läuft schon, während noch gepackt wird, und es läuft weiter,
 * wenn der Wagen längst auf dem Stellplatz steht - eine Nacht mit
 * eingeschaltetem Logger machte aus acht Stunden Schlaf acht Stunden Pause und
 * damit aus jedem künftigen Reisetag einen unbrauchbar langen.
 *
 * Erkannt wird das an den Rändern der Aufzeichnung: ein Halt, der mit dem
 * ersten Zeitstempel beginnt oder mit dem letzten endet, grenzt an nichts an
 * und kann deshalb nichts unterbrochen haben.
 *
 * Gerechnet wird vom fertigen stoppedSeconds abwärts und nicht über die
 * Halteliste aufwärts: die Liste ist auf MAX_STOPS gekürzt (lib/gpx.ts), ihre
 * Summe ist also nicht die Standzeit. Was gekürzt wurde, waren die kürzesten
 * Halte - ein Halt am Rand, auf den es hier ankommt, ist lang und steht noch
 * darin.
 */
export function pausenSekunden(track: DiaryTrack): number {
  const { stops, startedAt, endedAt, stoppedSeconds } = track
  if (startedAt === null || endedAt === null || stops.length === 0) return stoppedSeconds

  const erster = stops[0]!
  const letzter = stops[stops.length - 1]!

  let rand = 0
  if (erster.from <= startedAt) rand += erster.to - erster.from
  // Nicht ein zweites Mal, wenn die ganze Aufzeichnung aus einem einzigen Halt
  // besteht: dann ist der erste auch der letzte.
  if (letzter.to >= endedAt && letzter !== erster) rand += letzter.to - letzter.from

  return Math.max(0, stoppedSeconds - Math.round(rand / 1000))
}

/**
 * Die Halte, die tatsächlich eine Fahrt unterbrochen haben.
 *
 * Dieselbe Unterscheidung wie in pausenSekunden, nur als Liste statt als Summe:
 * ein Halt, der mit dem ersten Zeitstempel beginnt oder mit dem letzten endet,
 * grenzt an nichts an und kann deshalb nichts unterbrochen haben. Das Gerät
 * läuft schon, während noch gepackt wird, und es läuft weiter, wenn der Wagen
 * längst steht.
 *
 * Warum es beides gibt: die Summe wird vom fertigen stoppedSeconds abwärts
 * gerechnet, weil die Liste auf MAX_STOPS gekürzt ist und ihre Summe deshalb
 * nicht die Standzeit ist. Wer die Halte einzeln zeigen will - wo und wie lange
 * -, ist auf genau diese gekürzte Liste angewiesen; sie enthält die langen, und
 * um die geht es.
 */
export function halteUnterwegs(track: DiaryTrack): DiaryTrack['stops'] {
  const { stops, startedAt, endedAt } = track
  if (startedAt === null || endedAt === null) return stops
  return stops.filter((stop) => stop.from > startedAt && stop.to < endedAt)
}

/**
 * Die Strecke, an der die Vollständigkeit einer Aufzeichnung gemessen wird.
 *
 * Die berechnete Strasse, wenn es eine aktuelle gibt, sonst der Handwert der
 * Etappe. Ausdrücklich nicht realKm aus dem Tagebuch: der Knopf in der
 * Aufzeichnung schreibt genau die Kilometer der Aufzeichnung dorthin, und eine
 * Abdeckung gegen den eigenen Wert wäre immer hundert Prozent.
 *
 * @returns 0, wenn es nichts zu vergleichen gibt - eine Etappe ohne Strecke und
 *   ohne Handwert sagt nichts darüber, wie vollständig eine Datei ist.
 */
function planKm(stage: Stage): number {
  if (routeState(stage) === 'current') {
    const km = endlich(stage.route?.distanceKm)
    if (km > 0) return km
  }
  return Math.max(0, endlich(stage.km))
}

/** Die Fahrzeit, die der Dienst für diese Etappe ausweist - ohne Zuschlag. */
function dienstStunden(stage: Stage): number {
  if (routeState(stage) !== 'current') return 0
  return Math.max(0, endlich(stage.route?.durationH))
}

/** Ob diese Etappe hinter der Reise liegt: abgehakt oder aufgezeichnet. */
function schonGefahren(entry: DiaryEntry | undefined): boolean {
  return entry?.done === true || entry?.track !== undefined
}

/** Was die Uhr einer Aufzeichnung über eine Etappe sagt. */
export interface EtappenZeit {
  /**
   * Reine Fahrzeit in Stunden: nur, was als Fahrt gemessen wurde.
   *
   * Die Gesamtzeit ohne die erkannten Halte und ohne die Lücken zwischen zwei
   * Dateien, in denen das Fahrzeug den Ort gewechselt hat. Das ist die Zahl,
   * die mit der Planung verglichen und aus der hochgerechnet wird, und sie darf
   * deshalb keine Minute enthalten, für die es keine Messung gibt.
   */
  fahrtH: number
  /** Pausen unterwegs in Stunden, ohne den Stand vor und nach der Fahrt. */
  pausenH: number
  /** Die volle Standzeit in Stunden, so wie sie im Track steht. */
  standH: number
  /**
   * Zeit in Stunden, über die keine Aufzeichnung vorliegt.
   *
   * 0 bei jedem Track aus einer einzigen Datei - und das ist der Normalfall.
   * Was darin steckt, steht bei unrecordedSeconds in types.ts.
   */
  nichtAufgezeichnetH: number
  /**
   * Fahrt, Pausen und nicht aufgezeichnete Zeit zusammen: so lang war der Tag
   * zwischen Aufbruch und Ankunft.
   *
   * Nicht dasselbe wie totalSeconds im Track. Der zählt vom ersten bis zum
   * letzten Zeitstempel und nimmt damit auch die Stunden mit, in denen das
   * Gerät auf dem Stellplatz noch lief. Diese Zahl ist die ehrlichere Antwort
   * auf „wie lang wird so ein Tag“.
   *
   * Die Lücke gehört dazu, auch wenn niemand weiss, was in ihr geschah: sie
   * liegt zwischen Aufbruch und Ankunft, und der Tag war um sie länger. Was
   * sich geändert hat, ist nicht die Länge des Tages, sondern wovon sie sich
   * zusammensetzt.
   */
  tagH: number
  /**
   * Schnitt über die reine Fahrzeit, in km/h und auf ganze gerundet.
   *
   * Die Kilometer sind alle gefahrenen, auch die über eine Lücke hinweg
   * (distanceKm). Wo viel Zeit fehlt, steht der Schnitt deshalb zu hoch - und
   * genau diese Aufzeichnungen sind über zeitAbdeckung als unvollständig
   * gekennzeichnet.
   */
  schnittKmh: number
  /** Geplante reine Fahrzeit in Stunden: der Handwert der Etappe. */
  geplantH: number
  /**
   * Anteil der geplanten Strecke, den die Aufzeichnung abdeckt.
   *
   * null heisst „lässt sich nicht sagen“: die Etappe hat weder eine aktuelle
   * Strecke noch einen Handwert in Kilometern.
   */
  abdeckung: number | null
  /**
   * Anteil des Tages, über den überhaupt eine Aufzeichnung vorliegt.
   *
   * 1 ohne Lücke. Anders als bei den Kilometern gibt es hier immer eine
   * Antwort: die Spanne des Tages steht in der Datei, und was von ihr fehlt,
   * ebenfalls.
   */
  zeitAbdeckung: number
  /** Ob die Aufzeichnung genug abdeckt, um über die Etappe etwas zu sagen. */
  vollstaendig: boolean
}

/**
 * Was auf einer Etappe tatsächlich an Zeit verging.
 *
 * @returns undefined, wenn es dazu nichts zu sagen gibt: keine Aufzeichnung,
 *   keine Uhr in der Datei oder keine Fahrzeit darin. Ein geschätzter Wert
 *   wäre hier schlimmer als keiner - er stünde neben lauter gemessenen.
 */
export function etappenZeit(stage: Stage, entry?: DiaryEntry): EtappenZeit | undefined {
  const track = entry?.track
  if (!track || track.startedAt === null || track.endedAt === null) return undefined
  if (!(track.movingSeconds > 0)) return undefined

  const fahrtH = track.movingSeconds / STUNDE
  const pausenH = pausenSekunden(track) / STUNDE
  const ungemessen = Math.max(0, endlich(track.unrecordedSeconds))
  const nichtAufgezeichnetH = ungemessen / STUNDE
  const km = planKm(stage)
  const abdeckung = km > 0 ? runde2(track.distanceKm / km) : null
  const zeitAbdeckung =
    track.totalSeconds > 0 ? runde2((track.totalSeconds - ungemessen) / track.totalSeconds) : 1

  return {
    fahrtH: runde2(fahrtH),
    pausenH: runde2(pausenH),
    standH: runde2(track.stoppedSeconds / STUNDE),
    nichtAufgezeichnetH: runde2(nichtAufgezeichnetH),
    tagH: runde2(fahrtH + pausenH + nichtAufgezeichnetH),
    schnittKmh: Math.round(track.distanceKm / fahrtH),
    geplantH: Math.max(0, endlich(stage.h)),
    abdeckung,
    zeitAbdeckung,
    vollstaendig:
      abdeckung !== null && abdeckung >= MIN_ABDECKUNG && zeitAbdeckung >= MIN_ABDECKUNG,
  }
}

/** Was nach dem bisher Gefahrenen noch bevorsteht. */
export interface Restweg {
  /** Etappen ohne Haken und ohne Aufzeichnung, die eine aktuelle Strecke haben. */
  etappen: number
  /** Was für sie im Plan steht: die Summe der Handwerte in Stunden. */
  geplantH: number
  /** Fahrzeit laut Dienst für dieselben Etappen, ohne jeden Zuschlag. */
  dienstH: number
  /** Erwartete reine Fahrzeit: die des Dienstes mit dem gemessenen Faktor. */
  fahrtH: number
  /** Erwartete Pausen, nach dem gemessenen Verhältnis zur Fahrzeit. */
  pausenH: number
}

/** Wie sich die geschätzten Fahrzeiten der Reise gegen die gefahrenen halten. */
export interface Fahrzeitbilanz {
  /** Etappen mit einer Aufzeichnung, die eine Uhr und Fahrzeit mitbringt. */
  mitAufzeichnung: number
  /** Davon die, deren Aufzeichnung für einen Vergleich nicht taugt. */
  unvergleichbar: number
  /** Etappen, über die verglichen wird. */
  etappen: number
  /** Fahrzeit laut Dienst über diese Etappen, in Stunden und ohne Zuschlag. */
  dienstH: number
  /** Gemessene reine Fahrzeit über dieselben Etappen. */
  fahrtH: number
  /** Gemessene Pausen über dieselben Etappen. */
  pausenH: number
  /**
   * Gefahrene Fahrzeit geteilt durch die des Dienstes.
   *
   * 0 ohne belastbare Grundlage. Ein Faktor von 1.25 heisst: das Fahrzeug
   * braucht ein Viertel länger als der Personenwagen des Dienstes - genau das,
   * was der Zuschlag behauptet.
   */
  faktor: number
  /** Der eingetragene Zuschlag des Fahrzeugs, als Anteil. */
  zuschlagGeplant: number
  /** Der gemessene Zuschlag: faktor minus eins. 0 ohne Grundlage. */
  zuschlagGemessen: number
  /** Pause je Fahrstunde, als Anteil. 0 ohne Grundlage. */
  pauseJeFahrstunde: number
  /** Ob genug gemessen wurde, um daraus etwas abzuleiten. */
  belastbar: boolean
  /** Die Hochrechnung auf den Rest der Reise. null ohne Grundlage oder ohne Rest. */
  rest: Restweg | null
}

/**
 * Stellt die geschätzten Fahrzeiten der Reise den gefahrenen gegenüber.
 *
 * Verglichen wird über die Fahrzeit des Dienstes und nicht über den Handwert
 * der Etappe, obwohl der Handwert das ist, womit geplant wird. Der Grund ist
 * derselbe wie bei routeComparison in lib/trip.ts: der Handwert darf alles
 * sein, was jemand hineinschreibt, und ein Verhältnis zweier Handwerte misst
 * die Eintragsgewohnheiten und nicht das Fahrzeug. Die Fahrzeit des Dienstes
 * ist dagegen für jede Etappe nach derselben Regel entstanden - nur gegen sie
 * bedeutet ein Faktor überall dasselbe, und nur so ist er mit dem Zuschlag
 * vergleichbar, der genau auf ihr sitzt.
 *
 * Der Handwert steht trotzdem daneben, nämlich im Rest: dort ist er die Zahl,
 * mit der der Nutzer seinen Tag geplant hat, und die Hochrechnung ist nur
 * interessant, weil sie sich von ihr unterscheidet.
 *
 * Nicht aufgezeichnete Zeit geht in den Faktor nicht ein, und zwar zweimal
 * nicht: sie steckt nicht in der gemessenen Fahrzeit (siehe EtappenZeit.fahrtH),
 * und eine Etappe, an der viel davon hängt, ist über die Abdeckung gar nicht
 * erst vergleichbar. Beides zusammen ist nötig. Ohne das Erste zählte die Lücke
 * als Fahrt und machte das Fahrzeug langsamer, als es ist; ohne das Zweite
 * fehlte die in der Lücke gefahrene Zeit in der Fahrzeit und machte es
 * schneller. Was unterhalb der Schwelle bleibt, verschiebt den Faktor um
 * höchstens den Rest - eine Etappe, die zu neunzig Prozent aufgezeichnet ist,
 * kann nur ein Zehntel ihrer Zeit verstecken.
 *
 * Die Pausen gehen ausdrücklich nicht in den Faktor ein. Der Zuschlag sitzt auf
 * der Fahrzeit und verlängert jede Strecke, auch die Fahrt von zwanzig Minuten
 * zum Stellplatz; ein Mittagshalt hat damit nichts zu tun. Sie stehen deshalb
 * als eigene Zahl daneben - und das ist die ehrlichere Auskunft, denn im Plan
 * dieser App kommt eine Pause überhaupt nicht vor.
 */
export function fahrzeitbilanz(trip: Trip, diary: Record<string, DiaryEntry>): Fahrzeitbilanz {
  let mitAufzeichnung = 0
  let unvergleichbar = 0
  let etappen = 0
  let dienstH = 0
  let fahrtH = 0
  let pausenH = 0

  let restEtappen = 0
  let restGeplantH = 0
  let restDienstH = 0

  for (const stage of activeStages(trip)) {
    const entry = diary[stage.id]
    const zeit = etappenZeit(stage, entry)
    const dienst = dienstStunden(stage)

    if (zeit) {
      mitAufzeichnung++
      if (zeit.vollstaendig && dienst > 0) {
        etappen++
        dienstH += dienst
        fahrtH += zeit.fahrtH
        pausenH += zeit.pausenH
      } else {
        unvergleichbar++
      }
      continue
    }

    // Was noch bevorsteht. Eine abgehakte Etappe ohne brauchbare Aufzeichnung
    // gehört nicht dazu - sie ist gefahren, nur eben nicht gemessen.
    if (schonGefahren(entry) || dienst <= 0) continue
    restEtappen++
    restGeplantH += Math.max(0, endlich(stage.h))
    restDienstH += dienst
  }

  const belastbar = etappen >= EICHUNG_MIN_ETAPPEN && fahrtH >= EICHUNG_MIN_STUNDEN && dienstH > 0
  // Gerundet, bevor damit weitergerechnet wird: was in der Anzeige steht, soll
  // dieselbe Zahl sein, aus der die Hochrechnung entstanden ist.
  const faktor = belastbar ? runde2(fahrtH / dienstH) : 0
  const pauseJeFahrstunde = belastbar && fahrtH > 0 ? runde2(pausenH / fahrtH) : 0

  const restFahrtH = runde2(restDienstH * faktor)

  return {
    mitAufzeichnung,
    unvergleichbar,
    etappen,
    dienstH: runde2(dienstH),
    fahrtH: runde2(fahrtH),
    pausenH: runde2(pausenH),
    faktor,
    zuschlagGeplant: Math.max(0, endlich(fahrzeug(trip).surcharge)),
    zuschlagGemessen: belastbar ? runde2(faktor - 1) : 0,
    pauseJeFahrstunde,
    belastbar,
    rest:
      belastbar && restEtappen > 0
        ? {
            etappen: restEtappen,
            geplantH: runde2(restGeplantH),
            dienstH: runde2(restDienstH),
            fahrtH: restFahrtH,
            pausenH: runde2(restFahrtH * pauseJeFahrstunde),
          }
        : null,
  }
}

// --- Anzeige -----------------------------------------------------------------

/**
 * Eine Abweichung als Dauer mit Vorzeichen, etwa „+42 min“.
 *
 * Dieselbe Machart wie differenzGenau in features/map/etappenVergleich.ts: das
 * Vorzeichen ist die Aussage, und eine Null bleibt als ±0 stehen, statt als
 * „0 min“ auszusehen wie ein Messwert.
 *
 * Das Minus ist das typografische (U+2212) und nicht der Bindestrich - genau
 * wie dort, damit dieselbe Abweichung an beiden Stellen gleich aussieht.
 */
export function dauerAbweichung(sekunden: number): string {
  const minuten = Math.round(endlich(sekunden) / 60)
  if (minuten === 0) return '±0'
  const betrag = Math.abs(minuten)
  const h = Math.floor(betrag / 60)
  const rest = betrag % 60
  const text = h > 0 ? `${String(h)} h ${String(rest).padStart(2, '0')} min` : `${String(rest)} min`
  return `${minuten > 0 ? '+' : '−'}${text}`
}

/** Ein Anteil als Prozentangabe, etwa 0.18 → „18 %“. */
export function prozent(anteil: number): string {
  return `${String(Math.round(endlich(anteil) * 100))} %`
}
